import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Menu,
} from "@mui/material";

import FlagIcon from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import EllipsisTooltipText from "./EllipsisTooltipText";
import ActionCheckBox from "../../pages-new/Studio/ActionCheckBox";
import { StatusColors, StatusOptions, WaitOptions } from "../../pages-new/Studio/constants";
import {
  useUpdateWaitTimeMutation,
  useUpdateWaitTypeMutation,
  useUpdateFlowMutation,
  useSetStepMileStone,
  useRemoveStepMilestone,
  useRemoveInline,
  useRemoveConditionalLogic,
} from "../../useQuery";
import useSaveStepQuery from "../../useQuery/useSaveStepQuery";
import { useNotification } from "../../hooks";
import { Check, Delete, DeleteOutline, RemoveRedEye } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { setEditStep } from "../../Redux/TestAuthoring/TestAuthoring";
import TestDataConfig from "../../pages-new/Studio/Edit/TestDataConfig";
import { EditIcon } from "../icons";
import ConditionalLogicDetailsDialog from "./ConditionalLogicDetailsDialog";

const TestTableView = ({
  testCaseResults,
  handleEdit,
  isCheckbox,
  isReadOnly = false,
  handleCheckboxChange,
  checkedItems,
  onInlineTestCaseView,
}) => {
  const notify = useNotification();
  const [editingWaitTime, setEditingWaitTime] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [testDataModalOpen, setTestDataModalOpen] = useState(false);
  const dispatch = useDispatch();
  const editStep = useSelector((state) => state.authoringSlice.editStep);
  const [inlineMenuAnchor, setInlineMenuAnchor] = useState(null);
  const [condLogicStep, setCondLogicStep] = useState(null);

  const hasConditionalLogic = (item) =>
    Boolean((item?.condLogic && item.condLogic !== "none") || item?.condexp || item?.ifCondition);

  const handleOpenDataConfig = (item) => {
    dispatch(setEditStep(item));
    setTestDataModalOpen(true);
  };

  const handleCloseDataConfig = () => {
    setTestDataModalOpen(false);
    dispatch(setEditStep({}));
    queryClient.invalidateQueries(["latestRecordings"]);
    queryClient.invalidateQueries(["testCaseResults"]);
  };
  const [selectedStep, setSelectedStep] = useState(null);
  const [milestoneDescription, setMilestoneDescription] = useState("");
  const queryClient = useQueryClient();

  const { mutate: updateWaitTime } = useUpdateWaitTimeMutation({
    onSuccess: () => {
      notify("Wait time updated successfully", "success");
      setEditingWaitTime(null);
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error updating wait time: ${error.message}`, "error");
    },
  });

  const { mutate: removeInline, isPending: isRemoveInlinePending } = useRemoveInline({
    onSuccess: () => {
      notify("Inline removed successfully", "success");
      setInlineMenuAnchor(null);
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error removing inline: ${error.message}`, "error");
      setInlineMenuAnchor(null);
    },
  });

  const { mutate: removeConditionalLogic, isPending: isRemoveCondLogicPending } =
    useRemoveConditionalLogic({
      onSuccess: () => {
        notify("Conditional logic removed successfully", "success");
        setCondLogicStep(null);
        queryClient.invalidateQueries(["latestRecordings"]);
        queryClient.invalidateQueries(["testCaseResults"]);
      },
      onError: (error) => {
        notify(
          error?.response?.data?.message || `Error removing conditional logic: ${error.message}`,
          "error",
        );
      },
    });

  const { mutate: updateWaitType } = useUpdateWaitTypeMutation({
    onSuccess: () => {
      notify("Wait type updated successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error updating wait type: ${error.message}`, "error");
    },
  });

  const { mutate: updateFlow } = useUpdateFlowMutation({
    onSuccess: () => {
      notify("Flow status updated successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error updating flow status: ${error.message}`, "error");
    },
  });

  const { mutate: setMilestone } = useSetStepMileStone({
    onSuccess: () => {
      notify("Milestone added successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
      handleMilestoneClose();
    },
    onError: (error) => {
      notify(`Error adding milestone: ${error.message}`, "error");
    },
  });

  const { mutate: removeMilestone } = useRemoveStepMilestone({
    onSuccess: () => {
      notify("Milestone removed successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error removing milestone: ${error.message}`, "error");
    },
  });

  const { mutate: saveStep } = useSaveStepQuery({
    onSuccess: () => {
      notify("Changes saved successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
      queryClient.invalidateQueries(["testCaseResults"]);
      handleCloseDataConfig();
    },
    onError: (error) => {
      notify(`Error saving changes: ${error.message}`, "error");
    },
  });

  const handleWaitTimeChange = (item, value) => {
    if (value > 999) {
      notify("Maximum wait time allowed is 999", "error");

      value = 999;
    }

    setEditingWaitTime(item.idtest_step);
    setEditedValues({
      ...editedValues,
      [item.idtest_step]: {
        ...(editedValues[item.idtest_step] || item),
        waittime: value,
      },
    });
  };

  const handleWaitTypeChange = (item, value) => {
    updateWaitType({
      teststepid: item.idtest_step,
      tcid: item.Test_Case_Id,
      before: value,
    });
    setEditedValues({
      ...editedValues,
      [item.idtest_step]: {
        ...(editedValues[item.idtest_step] || item),
        wait: value,
      },
    });
  };

  const handleSaveWaitTime = (item) => {
    const editedItem = editedValues[item.idtest_step];
    if (editedItem?.waittime !== item.waittime) {
      updateWaitTime({
        tsid: item.idtest_step,
        tcid: item.Test_Case_Id,
        time: editedItem.waittime,
      });
    }
  };

  const handleFlowChange = (item, newFlow) => {
    updateFlow({
      teststepid: item.idtest_step,
      tcid: item.Test_Case_Id,
      flow: newFlow,
    });
  };

  const handleMilestoneClick = (item) => {
    // Add validation
    if (!item || !item.idtest_step) {
      notify("Invalid step data", "error");
      return;
    }

    if (item.ismilestone === "true") {
      removeMilestone(item.idtest_step);
      return;
    }

    setSelectedStep(item);
    setMilestoneDialogOpen(true);
  };

  const handleMilestoneSave = () => {
    if (!milestoneDescription.trim()) {
      notify("Please enter a milestone description", "warning");
      return;
    }

    // Add validation for selectedStep
    if (!selectedStep || !selectedStep.idtest_step || !selectedStep.Test_Case_Id) {
      notify("Invalid step selected", "error");
      handleMilestoneClose();
      return;
    }

    setMilestone({
      data: {
        testStepId: selectedStep.idtest_step,
        testCaseId: selectedStep.Test_Case_Id,
        milestoneDescription: milestoneDescription.trim(),
      },
    });
  };

  const handleMilestoneClose = () => {
    setMilestoneDescription("");
    setSelectedStep(null);
    setMilestoneDialogOpen(false);
  };

  const handleSaveAllChanges = () => {
    if (!editStep || !editStep.idtest_step) {
      notify("Invalid step data", "error");
      return;
    }

    const payload = {
      Keyword: editStep.Keyword,
      Action: editStep.Action,
      Flow: editStep.Flow,
      Page_Description: editStep.Page_Description,
      Page_Name: editStep.Page_Name,
      TestData: editStep?.TestData,
      Test_Case_Id: editStep.Test_Case_Id,
      tsid: editStep.idtest_step,
      wait: editStep.wait,
      waittime: editStep.waittime,
      subAction: editStep.subAction,
      teststepthreshold: editStep.teststepthreshold,
      iframexpath: editStep.iframexpath,
      tabid: editStep.tabid,
      VarName: editStep.VarName,
      type: editStep.type,
      isDynamic: editStep.isDynamic,
      createsAlert: editStep.createsAlert,
      ObjectIdentifier: editStep.Object_Xpath,
      inlinedepedence: editStep.inlinedepedence,
      condLogic: editStep.condLogic,
      condexp: editStep.condexp,
      stepExpression: editStep.stepExpression,
      initialization: editStep.initialization,
      terminateCondition: editStep.terminateCondition,
      ifCondition: editStep.ifCondition,
      assertionExpression: editStep.assertionExpression,
      testProcessingtype: editStep.testProcessingtype,
      testProcessingobj: editStep.testProcessingobj,
      updateRecordData: editStep.updateRecordData ?? false,
      backgroundColor: editStep.bgcolor,
      color: editStep.color,
    };

    saveStep(payload);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ mt: 3, boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {isCheckbox && <TableCell padding="checkbox" />}
              <TableCell>Step Number</TableCell>
              {/* <TableCell>Status</TableCell> */}
              {/* <TableCell>Screenshot</TableCell> */}
              <TableCell>Page Description</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Condition</TableCell>
              <TableCell>Test Data</TableCell>
              <TableCell>Flow</TableCell>
              <TableCell sx={{ width: "320px" }}>Wait Time</TableCell>
              <TableCell>Milestone</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testCaseResults?.data.map((item, index) => (
              <TableRow
                key={item.idtest_step}
                sx={{ backgroundColor: StatusColors[item.Flow] || "#ffffff" }}
              >
                {isCheckbox && (
                  <TableCell padding="checkbox">
                    <ActionCheckBox
                      handleCheckboxChange={handleCheckboxChange}
                      item={item}
                      checkedItems={checkedItems}
                    />
                  </TableCell>
                )}
                <TableCell>{item.Step_Number}</TableCell>
                {/* <TableCell>
                  <Box display="flex" alignItems="center">
                    {item.Issues ? <CircleWarningIcon /> : <CircleTickIcon />}
                  </Box>
                </TableCell> */}
                {/* <TableCell>
                  <Box
                    sx={{
                      width: 100,
                      height: 60,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    {tsrData &&
                    (tsrData[index]?.screenshot1 || tsrData[index]?.Failure_Screenshot_Location) ? (
                      <img
                        style={{ height: "100%", width: "100%", objectFit: "cover" }}
                        src={tsrData[index].screenshot1 || tsrData[index].Failure_Screenshot_Location}
                        alt="testImage"
                      />
                    ) : (
                      <ImageNotSupportedOutlinedIcon sx={{ color: "#8089A8" }} />
                    )}
                  </Box>
                </TableCell> */}
                <TableCell>
                  <EllipsisTooltipText
                    text={item.Page_Description || "-"}
                    maxChars={50}
                    mode="chars"
                  />
                </TableCell>
                <TableCell>
                  <Typography>{item.Action}</Typography>
                </TableCell>
                <TableCell>
                  {hasConditionalLogic(item) ? (
                    <Tooltip title="View Conditional Logic">
                      <Chip
                        label="Conditional"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCondLogicStep(item);
                        }}
                        sx={{
                          fontSize: "11px",
                          height: "24px",
                          cursor: "pointer",
                          color: "#9a3412",
                          backgroundColor: "#fff7ed",
                          border: "1px solid #fdba74",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#ffedd5",
                          },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      label="Always"
                      size="small"
                      sx={{
                        fontSize: "11px",
                        height: "24px",
                        color: "#6b7280",
                        backgroundColor: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EllipsisTooltipText text={item.TestData || "-"} maxChars={50} mode="chars" />
                    {!isReadOnly &&
                      !(item.testdata_source === "AsRecorded" && !item.ValDevice) &&
                      !(item.Keyword === "Assertion" && item.ValDevice === "OnScreen") && (
                        <Tooltip title="Configure Test Data Source">
                          <Chip
                            label={
                              item.Keyword === "Assertion" && item.ValDevice
                                ? item.ValDevice
                                : item.testdata_source
                                  ? item.testdata_source
                                  : "Configure"
                            }
                            size="small"
                            variant={item.testdata_source ? "outlined" : "filled"}
                            onClick={() => handleOpenDataConfig(item)}
                            sx={{
                              fontSize: "10px",
                              height: "20px",
                              cursor: "pointer",
                              color: "#547de8",
                              backgroundColor: "#eff4ff",
                              border: "1px solid #547de8",
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: "#dbeafe",
                                boxShadow: "0 0 2px rgba(84, 125, 232, 0.5)",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={item.Flow || ""}
                    onChange={(e) => handleFlowChange(item, e.target.value)}
                    sx={{ minWidth: "120px" }}
                    disabled={isReadOnly}
                  >
                    {StatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{
                        min: 0,
                        max: 999,
                      }}
                      value={
                        editingWaitTime === item.idtest_step
                          ? editedValues[item.idtest_step]?.waittime
                          : item.waittime || ""
                      }
                      onChange={(e) => handleWaitTimeChange(item, e.target.value)}
                      sx={{ width: "80px" }}
                      disabled={isReadOnly}
                    />
                    <Tooltip title={item.wait}>
                      <Select
                        size="small"
                        value={item.wait || "Before"}
                        onChange={(e) => handleWaitTypeChange(item, e.target.value)}
                        sx={{ width: "120px" }}
                        disabled={isReadOnly}
                      >
                        {WaitOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Tooltip>
                    {!isReadOnly &&
                      editingWaitTime === item.idtest_step &&
                      editedValues[item.idtest_step]?.waittime !== (item.waittime || "") && (
                        <IconButton
                          size="small"
                          onClick={() => handleSaveWaitTime(item)}
                          sx={{
                            border: "2px solid #547de8",
                            borderRadius: "88px",
                            width: 30,
                            height: 30,
                          }}
                        >
                          <Check />
                        </IconButton>
                      )}
                  </Box>
                </TableCell>
                <TableCell>
                  {item.ismilestone === "true" && (
                    <Chip
                      icon={<FlagIcon sx={{ fontSize: 16 }} />}
                      label={item.milestone_description || "Milestone"}
                      size="small"
                      deleteIcon={
                        !isReadOnly ? (
                          <Tooltip title="Remove Milestone">
                            <Delete sx={{ fontSize: 16 }} />
                          </Tooltip>
                        ) : undefined
                      }
                      onDelete={!isReadOnly ? () => handleMilestoneClick(item) : undefined}
                      sx={{
                        background: "transparent",
                        border: "none",
                        color: "inherit",
                        fontWeight: "inherit",
                        fontSize: "inherit",
                        padding: 0,
                        "& .MuiChip-icon": { color: "#547de8" },
                        "& .MuiChip-label": { paddingLeft: "4px", paddingRight: "4px" },
                        "& .MuiChip-deleteIcon": {
                          color: "#ff5336",
                          marginLeft: "4px",
                          "&:hover": {
                            color: "#cc3322",
                          },
                        },
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Edit Step">
                      <IconButton
                        onClick={() => handleEdit(item)}
                        sx={{
                          height: 40,
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {item.ismilestone !== "true" && (
                      <Tooltip title="Add Milestone">
                        <IconButton
                          onClick={() => handleMilestoneClick(item)}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: "transparent",
                            "&:hover": {
                              backgroundColor: "rgba(84, 125, 232, 0.1)",
                            },
                          }}
                        >
                          <FlagOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.Keyword === "Inline" && (
                      <Tooltip title="Inline Test Case Actions">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();

                            setInlineMenuAnchor({ element: e.currentTarget, item });
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                          }}
                        >
                          <RemoveRedEye />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={inlineMenuAnchor?.element}
        open={Boolean(inlineMenuAnchor)}
        onClose={() => setInlineMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            onInlineTestCaseView(inlineMenuAnchor?.item?.TestData);
            setInlineMenuAnchor(null);
          }}
          sx={{ fontSize: 14, gap: 1.5 }}
        >
          <RemoveRedEye sx={{ fontSize: 18, color: "#547de8" }} />
          View Inline Test Case
        </MenuItem>
        <MenuItem
          onClick={() => {
            removeInline({
              testStepId: inlineMenuAnchor?.item?.idtest_step,
              testCaseId: inlineMenuAnchor?.item?.Test_Case_Id,
            });
          }}
          disabled={isRemoveInlinePending}
          sx={{
            fontSize: 14,
            gap: 1.5,
            color: isRemoveInlinePending ? "#999" : "#ff5336",
            pointerEvents: isRemoveInlinePending ? "none" : "auto",
          }}
        >
          {isRemoveInlinePending ? (
            <CircularProgress size={18} sx={{ color: "#ff5336" }} />
          ) : (
            <DeleteOutline sx={{ fontSize: 18 }} />
          )}
          {isRemoveInlinePending ? "Removing..." : "Remove Inline Test Case"}
        </MenuItem>
      </Menu>
      <ConditionalLogicDetailsDialog
        open={Boolean(condLogicStep)}
        onClose={() => setCondLogicStep(null)}
        step={condLogicStep}
        onRemove={() => {
          const stepId = condLogicStep?.idtest_step;
          const testCaseId = condLogicStep?.Test_Case_Id;
          if (!stepId || !testCaseId) return;
          removeConditionalLogic({
            testCaseId,
            testStepIds: [stepId],
          });
        }}
        isRemoving={isRemoveCondLogicPending}
      />

      {/* Milestone Dialog */}
      <Dialog open={milestoneDialogOpen} onClose={handleMilestoneClose}>
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Milestone Description"
            fullWidth
            variant="outlined"
            value={milestoneDescription}
            onChange={(e) => setMilestoneDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMilestoneClose} sx={{ color: "#ff5336" }}>
            Cancel
          </Button>
          <Button onClick={handleMilestoneSave} variant="contained" sx={{ background: "#ff5336" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={testDataModalOpen} onClose={handleCloseDataConfig} maxWidth="md" fullWidth>
        <DialogTitle>Test Data Configuration - Step {editStep?.Step_Number}</DialogTitle>
        <DialogContent dividers>{editStep && <TestDataConfig item={editStep} />}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDataConfig} sx={{ color: "#ff5336" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAllChanges}
            variant="contained"
            sx={{
              background: "#ff5336",
              "&:hover": {
                background: "#e94a2f",
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestTableView;
